import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import Icons from '../components/Icons';
import toast from 'react-hot-toast';
import { IMG_MODELS, VID_MODELS } from '../constants/models';
import { MODEL_SYSTEM_PROMPTS } from '../constants/system_prompts';
import { confirmAlert } from '../utils/confirmAlert';

// ═══════════════════════════════════════════════════════════════════════════════
//  HOW TO ADD A NEW MODEL:
//  1. Add a new object to the right mode array (video / image / multishot)
//  2. Fill in: id, label, badge, examples, systemPrompt
//  3. systemPrompt is your custom backend structure
//  4. No other code changes needed
// ═══════════════════════════════════════════════════════════════════════════════
const MODEL_CONFIGS = {
  video: [
    {
      id: 'video-none',
      label: 'None (Default)',
      badge: '⚪',
      color: '#9ca3af',
      examples: [
        { lang: 'Bangla', text: 'একটি বিড়াল বল নিয়ে খেলছে' },
        { lang: 'English', text: 'A cat playing with a ball of yarn' },
      ],
      systemPrompt: `You are an AI video prompt engineer. Convert user ideas into clear, effective AI video generation prompts.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label VIDEO PROMPT:
- Focus clearly on the subject and action without forcing a specific style, unless requested tightly
- The prompt must be 50-80 words
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

VIDEO PROMPT:
[Full prompt 50-80 words]`
    },
    {
      id: 'video-cinematic',
      label: 'Cinematic Pro',
      badge: '🎬',
      color: '#5b6bff',
      examples: [
        { lang: 'Bangla', text: 'একটি সূর্যাস্তের দৃশ্য নদীর পাড়ে, ধীরে ধীরে ক্যামেরা প্যান করছে' },
        { lang: 'English', text: 'A mystical forest at dawn with glowing fireflies and soft fog rolling through ancient trees' },
      ],
      systemPrompt: `You are a world-class AI video prompt engineer. Your job is to convert user ideas (in any language) into perfectly structured, cinematic AI video generation prompts.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language (if Bangla, reply in Bangla; if English, reply in English; etc.)
- Then output under the label VIDEO PROMPT:
- The prompt must be 80-120 words, cinematic, specific, and detailed
- After the prompt, include CAMERA:, STYLE TAGS:, and NEGATIVE: sections
- All output must be in prompt style — no meta-commentary, no "here is your prompt"
- Be specific about subject, action, environment, camera movement, lighting, visual style

Output format:
[One acknowledgment sentence in user's language]

VIDEO PROMPT:
[Full cinematic prompt 80-120 words]

CAMERA: [movement + shot type]
STYLE TAGS: [comma-separated tags]
NEGATIVE: [what to avoid]`
    },
    {
      id: 'video-dynamic',
      label: 'Dynamic Motion',
      badge: '🏃',
      color: '#8b5cf6',
      examples: [
        { lang: 'Bangla', text: 'একটি গাড়ি তীব্র গতিতে পাহাড়ি রাস্তায় ছুটে চলেছে' },
        { lang: 'English', text: 'A cheetah sprinting through golden savanna grass in slow motion with dramatic dust clouds' },
      ],
      systemPrompt: `You are an expert in creating high-energy, motion-focused AI video prompts. You specialize in dynamic shots, fast-paced action, and kinetic energy.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label VIDEO PROMPT:
- Focus on movement, speed, energy, and dynamic camera work
- The prompt must be 80-120 words
- Include motion tags and emphasize camera dynamics
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

VIDEO PROMPT:
[Full dynamic motion prompt 80-120 words]

CAMERA: [movement + shot type — emphasize tracking, crane, or handheld]
STYLE TAGS: [comma-separated tags with motion emphasis]
NEGATIVE: [what to avoid]`
    },
    {
      id: 'video-mood',
      label: 'Mood & Atmosphere',
      badge: '🌙',
      color: '#6366f1',
      examples: [
        { lang: 'Bangla', text: 'বৃষ্টির রাতে একটি নিঃসঙ্গ রাস্তায় একজন মানুষ হেঁটে যাচ্ছে' },
        { lang: 'English', text: 'An abandoned lighthouse on a stormy cliff at night with crashing waves and flickering light' },
      ],
      systemPrompt: `You are a master at creating atmospheric, mood-driven AI video prompts. You excel at evoking emotion, tension, and cinematic beauty through lighting, weather, and ambiance.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label VIDEO PROMPT:
- Focus on atmosphere, lighting, weather, and emotional tone
- The prompt must be 80-120 words
- Emphasize color palette, mood lighting, and environmental storytelling
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

VIDEO PROMPT:
[Full atmospheric prompt 80-120 words]

CAMERA: [movement + shot type — emphasize slow, deliberate movements]
STYLE TAGS: [comma-separated tags with mood emphasis]
NEGATIVE: [what to avoid]`
    },
    {
      id: 'video-vfx',
      label: 'VFX & Fantasy',
      badge: '✨',
      color: '#a855f7',
      examples: [
        { lang: 'Bangla', text: 'একটি ড্রাগন আগুন ছুড়ে মারছে মধ্যযুগীয় দুর্গের উপর' },
        { lang: 'English', text: 'A portal opening in the sky above a futuristic city with energy beams and floating debris' },
      ],
      systemPrompt: `You are a VFX and fantasy prompt specialist. You create prompts for epic, otherworldly, and visually stunning AI video scenes with special effects, magic, and sci-fi elements.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label VIDEO PROMPT:
- Focus on visual effects, particle systems, magic, sci-fi, and fantasy elements
- The prompt must be 80-120 words
- Include detailed VFX descriptions and fantastical elements
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

VIDEO PROMPT:
[Full VFX/fantasy prompt 80-120 words]

CAMERA: [movement + shot type — emphasize epic wide shots and dramatic angles]
STYLE TAGS: [comma-separated tags with VFX emphasis]
NEGATIVE: [what to avoid]`
    },
    {
      id: 'video-product',
      label: 'Product Showcase',
      badge: '📦',
      color: '#7c3aed',
      examples: [
        { lang: 'Bangla', text: 'একটি নতুন স্মার্টফোন টেবিলে ঘুরছে, আলো প্রতিফলিত হচ্ছে' },
        { lang: 'English', text: 'A luxury watch rotating on a velvet surface with studio lighting and golden reflections' },
      ],
      systemPrompt: `You are a commercial product videography prompt expert. You create prompts for sleek, professional product showcase videos with studio-quality lighting and elegant camera work.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label VIDEO PROMPT:
- Focus on product details, studio lighting, reflections, and elegant motion
- The prompt must be 80-120 words
- Emphasize commercial quality, clean backgrounds, and professional aesthetics
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

VIDEO PROMPT:
[Full product showcase prompt 80-120 words]

CAMERA: [movement + shot type — emphasize smooth orbits and macro details]
STYLE TAGS: [comma-separated tags with commercial emphasis]
NEGATIVE: [what to avoid]`
    },
  ],
  image: [
    {
      id: 'image-none',
      label: 'None (Default)',
      badge: '⚪',
      color: '#9ca3af',
      examples: [
        { lang: 'Bangla', text: 'একটি পাখির ছবি' },
        { lang: 'English', text: 'A picture of a bird' },
      ],
      systemPrompt: `You are an AI image prompt engineer. Convert user ideas into clear AI image prompts.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label IMAGE PROMPT:
- Focus on accurately describing the subject and scene without adding unnecessary stylistic flair unless requested.
- Write as a natural language description, 30-80 words.
- Include ASPECT RATIO: after the prompt.
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

IMAGE PROMPT:
[Natural language description, 30-80 words]

ASPECT RATIO: [ratio recommendation]`
    },
    {
      id: 'image-photorealistic',
      label: 'Photorealistic',
      badge: '📸',
      color: '#10b981',
      examples: [
        { lang: 'Bangla', text: 'একটি বাঙালি মেয়ে শাড়ি পরে ফুলের বাগানে দাঁড়িয়ে আছে' },
        { lang: 'English', text: 'A portrait of an elderly fisherman mending nets at golden hour on a wooden dock' },
      ],
      systemPrompt: `You are a photorealistic image prompt engineer. You create natural language, creative-director-style prompts for AI image generation that look like real photographs.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label IMAGE PROMPT:
- Write as a natural language description (no keyword spam), 80-150 words
- Focus on photographic qualities: lens type, depth of field, lighting, color grading
- Include ASPECT RATIO: and STYLE REFERENCE: after the prompt
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

IMAGE PROMPT:
[Natural language creative director description, 80-150 words]

ASPECT RATIO: [ratio recommendation]
STYLE REFERENCE: [photographic style reference]`
    },
    {
      id: 'image-artistic',
      label: 'Artistic & Concept',
      badge: '🎨',
      color: '#059669',
      examples: [
        { lang: 'Bangla', text: 'একটি স্বপ্নের মতো শহর যেখানে বাড়িগুলো মেঘের উপর ভাসছে' },
        { lang: 'English', text: 'A surreal painting of a fish swimming through clouds above a sleeping village' },
      ],
      systemPrompt: `You are an artistic and concept art prompt specialist. You create prompts for stylized, painterly, and conceptual AI images — not photos, but art.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label IMAGE PROMPT:
- Write as a natural language art direction, 80-150 words
- Reference art styles, movements, and techniques (impressionism, surrealism, etc.)
- Include ASPECT RATIO: and STYLE REFERENCE: after the prompt
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

IMAGE PROMPT:
[Natural language art-direction description, 80-150 words]

ASPECT RATIO: [ratio recommendation]
STYLE REFERENCE: [art style reference]`
    },
    {
      id: 'image-keyword',
      label: 'Keyword Optimized',
      badge: '🏷️',
      color: '#34d399',
      examples: [
        { lang: 'Bangla', text: 'একটি সাইবারপাঙ্ক শহরের রাস্তা, নিয়ন আলো, বৃষ্টি' },
        { lang: 'English', text: 'Cyberpunk alley at night with neon signs, rain puddles, and a lone figure in a trenchcoat' },
      ],
      systemPrompt: `You are a keyword-optimized image prompt engineer. You create structured, tag-based prompts with quality parameters for AI image generation models that work best with keywords.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label IMAGE PROMPT:
- Use action verb + subject + setting + lighting + style + quality tags format
- Include parameter flags at the end (e.g. --ar, --q, --style)
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

IMAGE PROMPT:
[Action verb] + [subject] + [setting] + [lighting] + [style] + [quality tags] --[parameters]`
    },
    {
      id: 'image-character',
      label: 'Character Design',
      badge: '👤',
      color: '#6ee7b7',
      examples: [
        { lang: 'Bangla', text: 'একজন যোদ্ধা রাজকন্যা বর্ম পরে তলোয়ার ধরে আছে' },
        { lang: 'English', text: 'A cybernetic samurai with glowing blue eyes and a plasma katana in a dystopian Tokyo' },
      ],
      systemPrompt: `You are a character design prompt specialist. You create detailed character description prompts for AI image generation, focusing on anatomy, costume, expression, and personality.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label IMAGE PROMPT:
- Focus on: physical features, clothing/armor, accessories, pose, expression, background context
- Write as a natural language character brief, 80-150 words
- Include ASPECT RATIO: and STYLE REFERENCE:
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

IMAGE PROMPT:
[Detailed character description, 80-150 words]

ASPECT RATIO: [ratio recommendation — usually 2:3 or 3:4 for characters]
STYLE REFERENCE: [art/design style reference]`
    },
  ],
  multishot: [
    {
      id: 'multishot-none',
      label: 'None (Default)',
      badge: '⚪',
      color: '#9ca3af',
      examples: [
        { lang: 'Bangla', text: '২ শটে একজন মানুষ — হাঁটছে, তারপর হাসছে' },
        { lang: 'English', text: 'A 2-shot sequence: man walking, then man smiling' },
      ],
      systemPrompt: `You are a multi-shot storyboard prompt engineer. You create numbered sequences of connected, visually consistent prompts for AI video/image storyboards.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label MULTI-SHOT STORYBOARD:
- Always start with a CONSISTENCY ANCHOR block (character, location, lighting)
- Then numbered shots (SHOT 1, SHOT 2, etc.) each with frame description and action. Do not force unnecessary artistic flair.
- Maintain visual consistency across all shots
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

MULTI-SHOT STORYBOARD:

CONSISTENCY ANCHOR:
Character: [locked physical description]
Location: [locked environment]
Lighting: [locked lighting setup]

SHOT 1:
[Full prompt]

SHOT 2:
[Full prompt]

[Continue for all shots]`
    },
    {
      id: 'multishot-story',
      label: 'Story Sequence',
      badge: '📖',
      color: '#f59e0b',
      examples: [
        { lang: 'Bangla', text: '৩ শটে একটি ছোট গল্প — একটি মেয়ে সমুদ্রতীরে হাঁটছে, ঝিনুক খুঁজে পাচ্ছে, সূর্যাস্ত দেখছে' },
        { lang: 'English', text: 'A 4-shot story of a street musician: setting up, playing guitar, crowd gathering, final bow at sunset' },
      ],
      systemPrompt: `You are a multi-shot storyboard prompt engineer. You create numbered sequences of connected, visually consistent prompts for AI video/image storyboards.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label MULTI-SHOT STORYBOARD:
- Always start with a CONSISTENCY ANCHOR block (character, location, lighting)
- Then numbered shots (SHOT 1, SHOT 2, etc.) each with frame description, camera angle, action, transition
- Maintain visual consistency across all shots
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

MULTI-SHOT STORYBOARD:

CONSISTENCY ANCHOR:
Character: [locked physical description]
Location: [locked environment]
Lighting: [locked lighting setup]

SHOT 1 — [WIDE SHOT / MEDIUM / CLOSE-UP / DETAIL]:
[Full prompt for this shot]

SHOT 2 — [...]:
[Full prompt]

[Continue for all shots]`
    },
    {
      id: 'multishot-ad',
      label: 'Ad Campaign',
      badge: '📢',
      color: '#d97706',
      examples: [
        { lang: 'Bangla', text: '৩ শটে একটি পারফিউম বিজ্ঞাপন — বোতল ক্লোজ-আপ, মডেল স্প্রে করছে, ধোঁয়া ও আলো' },
        { lang: 'English', text: 'A 3-shot luxury watch ad: extreme close-up of mechanism, wrist shot on a yacht, final hero shot with brand atmosphere' },
      ],
      systemPrompt: `You are an advertising storyboard prompt specialist. You create multi-shot sequences for commercial ads, product reveals, and brand campaigns.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label MULTI-SHOT STORYBOARD:
- Always start with a CONSISTENCY ANCHOR block
- Focus on commercial impact: product hero shots, lifestyle context, emotional payoff
- Each shot should have clear commercial purpose
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

MULTI-SHOT STORYBOARD:

CONSISTENCY ANCHOR:
Character: [locked physical description or product description]
Location: [locked environment]
Lighting: [locked lighting setup — commercial grade]

SHOT 1 — [SHOT TYPE]:
[Full commercial prompt for this shot]

SHOT 2 — [...]:
[Full prompt]

[Continue for all shots]`
    },
    {
      id: 'multishot-cinematic',
      label: 'Cinematic Sequence',
      badge: '🎥',
      color: '#fbbf24',
      examples: [
        { lang: 'Bangla', text: '৪ শটে একটি রহস্যময় দৃশ্য — অন্ধকার করিডোর, দরজা খুলছে, আলো বের হচ্ছে, চরিত্র প্রবেশ করছে' },
        { lang: 'English', text: 'A 5-shot cinematic chase: aerial city view, car speeding through alley, close-up driver, obstacle ahead, dramatic drift' },
      ],
      systemPrompt: `You are a cinematic sequence prompt engineer. You create multi-shot film-style storyboards with professional camera work, dramatic pacing, and visual storytelling.

Rules:
- Accept input in ANY language, always output the structured prompt in English
- Start with exactly ONE acknowledgment sentence in the user's language
- Then output under the label MULTI-SHOT STORYBOARD:
- Always start with a CONSISTENCY ANCHOR block
- Focus on cinematic techniques: establishing shots, coverage, match cuts, dramatic reveals
- Each shot should specify exact camera work
- No meta-commentary

Output format:
[One acknowledgment sentence in user's language]

MULTI-SHOT STORYBOARD:

CONSISTENCY ANCHOR:
Character: [locked physical description]
Location: [locked environment]
Lighting: [locked lighting setup]

SHOT 1 — [ESTABLISHING / WIDE SHOT]:
[Full cinematic prompt]

SHOT 2 — [...]:
[Full prompt with camera movement and transition notes]

[Continue for all shots]`
    },
  ],
};

const MODE_COLORS = {
  image: '#10b981',
  video: '#5b6bff',
  multishot: '#f59e0b',
};

const MODE_LABELS = {
  image: 'Image',
  video: 'Video',
  multishot: 'Multi-Shot',
};

const MODE_ICONS = {
  image: sz => <Icons.ImgFrame sz={sz||14} />,
  video: sz => <Icons.FilmStrip sz={sz||14} />,
  multishot: sz => <Icons.Multishot sz={sz||14} />,
};

// ── Auto-naming logic ────────────────────────────────────────────────────────
function generateName(text, mode) {
  const trimmed = text.trim().slice(0, 80);
  const words = trimmed.split(/\s+/);
  const filler = ['a','an','the','in','at','of','for','with','and','or','is','are'];
  const filtered = words.filter(w => !filler.includes(w.toLowerCase()));
  const name = filtered.slice(0, 5).join(' ');
  if (!name) return `${MODE_LABELS[mode] || mode} Prompt`;
  return name.length > 32 ? name.slice(0, 32) + '…' : name;
}



// ── Relative time helper ─────────────────────────────────────────────────────
function relativeTime(ts) {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

const API = process.env.REACT_APP_API_URL || '/api';

export default function PromptPage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { tokens, setTokens, setCredits, refresh } = useCredits();
  const location = useLocation();
  const navigate = useNavigate();

  // Always-current token ref so callbacks don't re-create on token rotation
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // ── State ────────
  const [mode, setMode] = useState('image');
  const [selectedModelIdx, setSelectedModelIdx] = useState(0);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [selectedRealModelId, setSelectedRealModelId] = useState('');
  const [showRealModelMenu, setShowRealModelMenu] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiOutput, setAiOutput] = useState('');
  const [selectedRefs, setSelectedRefs] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [activeHistoryItem, setActiveHistoryItem] = useState(null);
  const [generatedMode, setGeneratedMode] = useState(null);
  const [mobileTab, setMobileTab] = useState('writer'); // 'writer' | 'result' | 'history'

  // History from Backend (loaded via CreditContext)
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Copy feedback
  const [copied, setCopied] = useState(false);
  
  // Custom clear confirmation popup
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const [autoStartPrompt, setAutoStartPrompt] = useState(null);

  useEffect(() => {
    if (location.state?.prompt) {
      setUserInput(location.state.prompt);
      setAutoStartPrompt(location.state.prompt);
      
      if (location.state?.mode) setMode(location.state.mode);
      if (location.state?.modelId) setSelectedRealModelId(location.state.modelId);
      
      // Clear state so it doesn't re-run on reload
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const textareaRef = useRef(null);
  const outputRef = useRef(null);
  const abortRef = useRef(null);
  const genLockRef = useRef(false);

  const models = MODEL_CONFIGS[mode] || [];
  const currentModel = models[selectedModelIdx] || models[0];

  const realModels = mode === 'image' ? IMG_MODELS : VID_MODELS;
  const currentRealModel = realModels.find(m => m.id === (selectedRealModelId || activeHistoryItem?.realModelId)) || realModels[0];

  // ── Load history from backend on mount only (not on token rotation) ─────
  const loadHistory = useCallback(async () => {
    const tok = tokenRef.current;
    if (!tok) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API}/prompt/history`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (e) {
      console.error('Failed to load prompt history:', e);
    } finally {
      setHistoryLoading(false);
    }
  }, []); // No token dependency — reads tokenRef

  useEffect(() => {
    if (user) loadHistory();
  }, [user]); // Only re-load on actual login, not token rotation

  // ── Mode / model change resets ────────────────────────────────────────────
  useEffect(() => {
    setSelectedModelIdx(0);
    setSelectedRefs(0);
  }, [mode]);

  // ── Textarea auto-resize ──────────────────────────────────────────────────
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [userInput]);

  // ── Generate prompt (streaming SSE) ───────────────────────────────────────
  const handleGenerate = async (inputOverride) => {
    if (genLockRef.current) return;
    const input = inputOverride || userInput;
    if (!input.trim()) return;
    if (!user) { if (onShowAuth) onShowAuth(); return; }

    genLockRef.current = true;
    setShowResult(true);
    setActiveHistoryItem(null);
    setAiOutput('');
    setIsGenerating(true);
    setGeneratedMode(mode);
    setMobileTab('result'); // Auto-switch to result tab on mobile

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      let baseSystemPrompt = MODEL_SYSTEM_PROMPTS[currentRealModel.id] || currentModel.systemPrompt;
      
      if (currentRealModel.maxRefs > 0 && selectedRefs > 0) {
        baseSystemPrompt += `\n\nCRITICAL CONTEXT: The user will provide ${selectedRefs} reference image(s). Ensure you properly formulate the prompt to specify exactly how these ${selectedRefs} reference images should be applied (e.g., pose, face, style, background) based on their instructions. Fill out the reference fields carefully.`;
      } else if (currentRealModel.maxRefs > 0) {
        baseSystemPrompt += `\n\nCRITICAL CONTEXT: NO REFERENCE IMAGES are being used. Do not include any instructions or fields relating to reference images.`;
      }

      const res = await fetch(`${API}/prompt/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userInput: input,
          mode,
          modelId: currentRealModel.id, // Target AI model name/ID
          systemPrompt: baseSystemPrompt,
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `API error ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            if (dataStr === '[DONE]') continue;
            try {
              const parsed = JSON.parse(dataStr);
              const delta = parsed.choices?.[0]?.delta?.content || '';
              if (delta) {
                fullText += delta;
                setAiOutput(fullText);
              }
            } catch {}
          }
        }
      }

      // Save to history via backend
      if (fullText) {
        const name = generateName(input, mode);
        try {
          await fetch(`${API}/prompt/save`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              name,
              mode,
              modelId: currentRealModel.id,
              modelLabel: currentRealModel.label,
              styleId: currentModel.id,
              styleLabel: currentModel.label,
              userInput: input,
              aiOutput: fullText,
            }),
          });
          loadHistory();
        } catch (e) {
          console.error('Failed to save prompt:', e);
        }
        // Refresh credits+tokens from server (backend deducts after stream end)
        setTimeout(() => refresh(true), 1500);
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setAiOutput(`❌ Error: ${e.message}`);
        toast.error(e.message || 'Generation failed');
      }
    } finally {
      genLockRef.current = false;
      setIsGenerating(false);
      abortRef.current = null;
    }
  };

  // ── Auto-generate when arriving via Optimize redirect ────────────────────
  useEffect(() => {
    if (autoStartPrompt && user) {
      const promptToRun = autoStartPrompt;
      setAutoStartPrompt(null); // consume once
      // small delay so state is flushed before we start streaming
      setTimeout(() => handleGenerate(promptToRun), 120);
    }
  }, [autoStartPrompt, user]);

  // ── Copy to clipboard ─────────────────────────────────────────────────────
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Click history item ────────────────────────────────────────────────────
  const handleHistoryClick = (item) => {
    setActiveHistoryItem(item);
    setShowResult(true);
    setUserInput(item.userInput || '');
    setMobileTab('result');
  };

  const handleAccept = (text) => {
    const targetMode = activeHistoryItem?.mode || generatedMode || mode;
    const targetModelId = activeHistoryItem?.modelId || currentRealModel?.id;

    if (targetMode === 'video' || targetMode === 'multishot') {
      navigate('/video', { 
        state: { 
          prompt: text, 
          modelId: targetModelId, 
          isMultiShot: targetMode === 'multishot' 
        } 
      });
    } else {
      navigate('/', { 
        state: { 
          optimizedPrompt: text, 
          modelId: targetModelId 
        } 
      });
    }
  };

  // ── New prompt ────────────────────────────────────────────────────────────
  const handleNewPrompt = () => {
    setShowResult(false);
    setAiOutput('');
    setUserInput('');
    setActiveHistoryItem(null);
    if (abortRef.current) abortRef.current.abort();
  };

  // ── Delete history item ───────────────────────────────────────────────────
  const handleDeleteHistory = async (id, e) => {
    e.stopPropagation();
    const isConfirmed = await confirmAlert({
      title: 'Delete Prompt?',
      message: 'Are you sure you want to delete this prompt from your history?'
    });
    if (!isConfirmed) return;
    try {
      await fetch(`${API}/prompt/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(prev => prev.filter(h => h.$id !== id && h.id !== id));
      if (activeHistoryItem && (activeHistoryItem.$id === id || activeHistoryItem.id === id)) {
        handleNewPrompt();
      }
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  // ── Clear all history ─────────────────────────────────────────────────────
  const handleClearAll = async () => {
    try {
      await fetch(`${API}/prompt/clear`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory([]);
      handleNewPrompt();
      toast.success('History cleared');
    } catch {
      toast.error('Failed to clear history');
    }
  };

  // ── Example click ─────────────────────────────────────────────────────────
  const handleExampleClick = (text) => {
    setUserInput(text);
    handleGenerate(text);
  };

  // ── Parse prompt output into sections ─────────────────────────────────────
  const parseOutput = (text) => {
    if (!text) return { ack: '', body: '' };
    const labels = ['VIDEO PROMPT:', 'IMAGE PROMPT:', 'MULTI-SHOT STORYBOARD:'];
    let splitIdx = -1;
    let foundLabel = '';
    for (const label of labels) {
      const idx = text.indexOf(label);
      if (idx !== -1) { splitIdx = idx; foundLabel = label; break; }
    }
    if (splitIdx === -1) return { ack: '', body: text };
    return {
      ack: text.slice(0, splitIdx).trim(),
      label: foundLabel,
      body: text.slice(splitIdx).trim(),
    };
  };

  const displayOutput = activeHistoryItem ? activeHistoryItem.aiOutput : aiOutput;
  const displayInput = activeHistoryItem ? activeHistoryItem.userInput : userInput;
  const displayMode = activeHistoryItem?.mode || generatedMode || mode;
  const parsed = parseOutput(displayOutput);

  return (
    <>
      {/* ── Settings Panel (Left) ── */}
      <section className={`panel prompt-panel${mobileTab !== 'writer' ? ' prompt-mob-hidden' : ''}`} style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="phead"><span style={{ marginRight: 8 }}>✍️</span> Prompt Writer</div>

        {/* ── Token Balance Bar ── */}
        <div style={{
          margin: '0 0 14px',
          padding: '12px 14px',
          borderRadius: 12,
          background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.08) 100%)',
          border: '1px solid rgba(99,102,241,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🪙</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#a5b4fc' }}>
                {tokens.toLocaleString()} Tokens
              </div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 1 }}>
                0.2/word input · 0.8/word output
              </div>
            </div>
          </div>
          <button
            disabled={tokens >= 200}
            onClick={async () => {
              if (tokens >= 200) return;
              if (!user) { if (onShowAuth) onShowAuth(); return; }
              try {
                const res = await fetch(`${API}/tokens/buy`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Failed');
                setTokens(data.tokens);
                setCredits(data.credits);
                toast.success('✅ 20,000 tokens added!');
              } catch (e) {
                toast.error(e.message || 'Not enough credits (need 5)');
              }
            }}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
              background: tokens >= 200 ? 'rgba(99,102,241,0.05)' : 'rgba(99,102,241,0.2)',
              border: `1px solid ${tokens >= 200 ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.35)'}`,
              color: tokens >= 200 ? 'rgba(165,180,252,0.4)' : '#a5b4fc',
              cursor: tokens >= 200 ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all .2s',
            }}
            title={tokens >= 200 ? "Use some tokens before buying more" : "Buy tokens"}
          >
            +20K for 5 cr
          </button>
        </div>

        {/* Mode Tabs */}
        <div className="psec">
          <div className="slbl">Mode</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {Object.keys(MODE_LABELS).map(m => (
              <button
                key={m}
                className={`edit-mode-tab ${mode === m ? 'on' : ''}`}
                onClick={() => setMode(m)}
                style={mode === m ? { borderColor: MODE_COLORS[m], background: `${MODE_COLORS[m]}12` } : {}}
              >
                <span className="emt-icon">{MODE_ICONS[m] ? MODE_ICONS[m](14) : null}</span>
                <span className="emt-label" style={mode === m ? { color: MODE_COLORS[m] } : {}}>{MODE_LABELS[m]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Target Real Model Selection */}
        <div className="psec" style={{ position: 'relative' }}>
          <div className="slbl">Model</div>
          <div
            onClick={() => setShowRealModelMenu(!showRealModelMenu)}
            style={{
              padding: '10px 14px', borderRadius: 'var(--r)',
              border: `1px solid ${showRealModelMenu ? currentRealModel?.color : 'var(--b2)'}`,
              background: 'var(--s2)', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', cursor: 'pointer',
              userSelect: 'none', transition: 'var(--transition)',
              boxShadow: showRealModelMenu ? `0 0 0 1px ${currentRealModel?.color}, 0 4px 16px ${currentRealModel?.color}25` : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 24, height: 24, borderRadius: 6,
                background: `${currentRealModel?.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: currentRealModel?.color
              }}>
                {Icons[currentRealModel?.icon] ? React.createElement(Icons[currentRealModel.icon], { sz: 14 }) : <Icons.Sparkle sz={14} />}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{currentRealModel?.label}</span>
                <span style={{ fontSize: 10, color: 'var(--t3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: currentRealModel?.color }} />
                  {MODE_LABELS[mode]} Model
                </span>
              </div>
            </div>
            <span style={{ color: 'var(--t3)', fontSize: 10, transition: 'transform 0.2s', transform: showRealModelMenu ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              ▼
            </span>
          </div>

          {showRealModelMenu && (
            <>
              {/* Invisible overlay */}
              <div 
                onClick={() => setShowRealModelMenu(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 9 }}
              />
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                background: 'var(--s1)', border: '1px solid var(--b2)',
                borderRadius: 'var(--r)', zIndex: 10, padding: 6,
                display: 'flex', flexDirection: 'column', gap: 4,
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                maxHeight: '300px', overflowY: 'auto'
              }}>
                {realModels.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => { setSelectedRealModelId(m.id); setShowRealModelMenu(false); }}
                    style={{
                      padding: '8px 12px', borderRadius: 'var(--r2)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: currentRealModel?.id === m.id ? `${m.color}15` : 'transparent',
                      border: `1px solid ${currentRealModel?.id === m.id ? m.color : 'transparent'}`,
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={e => { if (currentRealModel?.id !== m.id) e.currentTarget.style.background = 'var(--s2)'; }}
                    onMouseLeave={e => { if (currentRealModel?.id !== m.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{
                      width: 20, height: 20, borderRadius: 4,
                      background: `${m.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: m.color
                    }}>
                      {Icons[m.icon] ? React.createElement(Icons[m.icon], { sz: 12 }) : <Icons.Sparkle sz={12} />}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: currentRealModel?.id === m.id ? m.color : 'var(--t1)' }}>
                        {m.label}
                      </span>
                    </div>
                    {currentRealModel?.id === m.id && (
                      <span style={{ marginLeft: 'auto', color: m.color, fontSize: 13, fontWeight: 'bold' }}>✓</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Style Selection (Previously "Model Selection") */}
        <div className="psec" style={{ position: 'relative' }}>
          <div className="slbl">Style</div>
          <div
            onClick={() => setShowModelMenu(!showModelMenu)}
            style={{
              padding: '10px 14px', borderRadius: 'var(--r)',
              border: `1px solid ${showModelMenu ? currentModel?.color : 'var(--b2)'}`,
              background: 'var(--s2)', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', cursor: 'pointer',
              userSelect: 'none', transition: 'var(--transition)',
              boxShadow: showModelMenu ? `0 0 0 1px ${currentModel?.color}, 0 4px 16px ${currentModel?.color}25` : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>{currentModel?.badge}</span>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{currentModel?.label}</span>
                <span style={{ fontSize: 10, color: 'var(--t3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: currentModel?.color }} />
                  {MODE_LABELS[mode]}
                </span>
              </div>
            </div>
            <span style={{ color: 'var(--t3)', fontSize: 10, transition: 'transform 0.2s', transform: showModelMenu ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              ▼
            </span>
          </div>

          {showModelMenu && (
            <>
              {/* Invisible overlay to catch outside clicks */}
              <div 
                onClick={() => setShowModelMenu(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 9 }}
              />
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                background: 'var(--s1)', border: '1px solid var(--b2)',
                borderRadius: 'var(--r)', zIndex: 10, padding: 6,
                display: 'flex', flexDirection: 'column', gap: 4,
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                maxHeight: '300px', overflowY: 'auto'
              }}>
                {models.map((m, idx) => (
                  <div
                    key={m.id}
                    onClick={() => { setSelectedModelIdx(idx); setShowModelMenu(false); }}
                    style={{
                      padding: '8px 12px', borderRadius: 'var(--r2)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: selectedModelIdx === idx ? `${m.color}15` : 'transparent',
                      border: `1px solid ${selectedModelIdx === idx ? m.color : 'transparent'}`,
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={e => { if (selectedModelIdx !== idx) e.currentTarget.style.background = 'var(--s2)'; }}
                    onMouseLeave={e => { if (selectedModelIdx !== idx) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: 16 }}>{m.badge}</span>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: selectedModelIdx === idx ? m.color : 'var(--t1)' }}>
                        {m.label}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--t3)', opacity: 0.8 }}>
                        {MODE_LABELS[mode]} Style
                      </span>
                    </div>
                    {selectedModelIdx === idx && (
                      <span style={{ marginLeft: 'auto', color: m.color, fontSize: 13, fontWeight: 'bold' }}>✓</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>


        {/* Prompt Input */}
        <div className="psec" style={{ flex: 1, borderBottom: 'none' }}>
          <div className="slbl">Your Idea</div>
          <div className="pwrap">
            <textarea
              ref={textareaRef}
              className="ptxt"
              placeholder="Type your idea in any language (English, Bangla, Hindi, Arabic…) and we'll generate a perfect prompt for you"
              value={userInput}
              onChange={e => {
                setUserInput(e.target.value);
                const target = e.target;
                target.style.height = '0px';
                target.style.height = target.scrollHeight + 'px';
              }}
              maxLength={10000}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleGenerate();
                }
              }}
              style={{ minHeight: '120px', fontSize: '13px', resize: 'none', overflowY: 'hidden', flex: 'none', display: 'block' }}
              disabled={isGenerating}
            />
            <div className="pbot">
              <span className="pcnt" style={{ fontSize: '10px' }}>{userInput.length}/10000</span>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <div className="gen-sticky">

          <button
            className={`gbtn ${isGenerating ? 'ld' : ''}`}
            onClick={() => handleGenerate()}
            disabled={!userInput.trim() || isGenerating}
          >
            <Icons.Sparkle sz={15} />
            {isGenerating ? 'Generating…' : 'Generate Prompt'}
          </button>
        </div>
      </section>

      {/* ── Main Canvas (Right) ── */}
      <div className={`main prompt-main-wrapper${mobileTab === 'writer' ? ' prompt-mob-hidden' : ''}`}>
        <div className="mtbar">
          <div className="mtab on">
            {showResult ? (activeHistoryItem ? '📜 History View' : '📄 Result') : '✍️ Prompt Writer'}
          </div>
          {showResult && activeHistoryItem && isGenerating && (
            <div className="mtab" onClick={() => setActiveHistoryItem(null)} style={{ cursor: 'pointer', color: 'var(--ac3)' }}>
              ← Active Generation
            </div>
          )}
          {showResult && !isGenerating && (
            <div className="mtab" onClick={handleNewPrompt} style={{ cursor: 'pointer', color: 'var(--ac3)' }}>
              ← New Prompt
            </div>
          )}
          {showResult && isGenerating && !activeHistoryItem && !aiOutput && (
            <div className="mtab" onClick={handleNewPrompt} style={{ cursor: 'pointer', color: '#ef4444' }}>
              ✕ Cancel Generation
            </div>
          )}
          <div style={{ flex: 1 }} />
        </div>

        <div className="mbody prompt-mbody" style={{ display: 'flex', gap: 20 }}>
          {/* ── Main Content Area ── */}
          <div className={`prompt-main-col${mobileTab !== 'result' ? ' prompt-mob-hidden' : ''}`} style={{ flex: 1, minWidth: 0 }}>
            {!showResult ? (
              /* ── Welcome Screen ── */
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 20 }}>
                <img src="/acon.png" alt="aiField" style={{ width: 64, height: 64, objectFit: 'contain', opacity: 0.25, marginBottom: 4 }} />
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--t1)' }}>
                  {MODE_LABELS[mode]} Prompt Writer
                </div>
                <div style={{ fontSize: 13, color: 'var(--t3)', maxWidth: 440, textAlign: 'center', lineHeight: 1.7 }}>
                  Type your idea in any language and we'll convert it into a perfectly structured{' '}
                  {mode === 'multishot' ? 'multi-shot storyboard' : `${MODE_LABELS[mode].toLowerCase()} generation`} prompt.
                </div>

                {/* Example prompts */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, width: '100%', maxWidth: 520 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>
                    Try an example
                  </div>
                  {currentModel?.examples?.map((ex, idx) => (
                    <button
                      key={idx}
                      className="prompt-example-btn"
                      onClick={() => handleExampleClick(ex.text)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '12px 16px', borderRadius: 'var(--r)',
                        border: '1px solid var(--b2)', background: 'var(--s2)',
                        color: 'var(--t2)', fontSize: 12, cursor: 'pointer',
                        textAlign: 'left', transition: 'var(--transition)',
                        lineHeight: 1.5,
                      }}
                    >
                      <span style={{
                        fontSize: 8, fontWeight: 800, padding: '2px 6px', borderRadius: 99,
                        background: `${MODE_COLORS[mode]}18`, color: MODE_COLORS[mode],
                        flexShrink: 0,
                      }}>
                        {ex.lang}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : isGenerating && !aiOutput && !activeHistoryItem ? (
              /* ── Loading State ── */
              <div className="prev-pending" style={{ minHeight: '40vh' }}>
                <div className="pend-icon"><Icons.PenBox sz={28} /></div>
                <div className="pend-label">Generating your prompt…</div>
                <div className="pend-sub">Using {currentModel?.label || 'AI'}</div>
                <div className="pend-bar-wrap"><div className="pend-bar" /></div>
              </div>
            ) : (
              /* ── Result Screen ── */
              <div style={{ animation: 'moin .3s ease-out' }}>
                {/* User input display */}
                <div style={{
                  padding: '12px 16px', borderRadius: 'var(--r)',
                  background: 'var(--s2)', border: '1px solid var(--b1)',
                  marginBottom: 16,
                }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>
                    Your idea
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--t2)', lineHeight: 1.6 }}>
                    {displayInput}
                  </div>
                </div>

                {/* Prompt Card */}
                <div style={{
                  border: `1px solid ${MODE_COLORS[displayMode]}30`,
                  borderRadius: 'var(--r2)',
                  background: `${MODE_COLORS[displayMode]}08`,
                  overflow: 'hidden',
                }}>
                  {/* Card header */}
                  <div style={{
                    padding: '10px 16px',
                    background: `${MODE_COLORS[displayMode]}12`,
                    borderBottom: `1px solid ${MODE_COLORS[displayMode]}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: MODE_COLORS[displayMode], textTransform: 'uppercase', letterSpacing: '.06em' }}>
                      {parsed.label || `${MODE_LABELS[displayMode]} Prompt`}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => handleCopy(parsed.body || displayOutput)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '4px 12px', borderRadius: 99,
                          border: `1px solid ${copied ? 'rgba(16,185,129,.3)' : `${MODE_COLORS[displayMode]}30`}`,
                          background: copied ? 'rgba(16,185,129,.1)' : 'transparent',
                          color: copied ? '#1fd1a0' : MODE_COLORS[displayMode],
                          fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          transition: 'var(--transition)',
                        }}
                      >
                        {copied ? '✓ Copied' : '📋 Copy'}
                      </button>
                      
                      <button
                        onClick={() => handleAccept(parsed.body || displayOutput)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '4px 12px', borderRadius: 99,
                          border: `1px solid ${MODE_COLORS[displayMode]}30`,
                          background: `${MODE_COLORS[displayMode]}15`,
                          color: MODE_COLORS[displayMode],
                          fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          transition: 'var(--transition)',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = `${MODE_COLORS[displayMode]}25`}
                        onMouseLeave={e => e.currentTarget.style.background = `${MODE_COLORS[displayMode]}15`}
                      >
                        ✨ Accept
                      </button>
                    </div>
                  </div>

                  {/* Acknowledgment */}
                  {parsed.ack && (
                    <div style={{
                      padding: '10px 16px 0', fontSize: 12,
                      color: 'var(--t3)', fontStyle: 'italic',
                    }}>
                      {parsed.ack}
                    </div>
                  )}

                  {/* Prompt body */}
                  <div ref={outputRef} style={{
                    padding: '14px 16px 16px',
                    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                    fontSize: 12.5, lineHeight: 1.8,
                    color: '#b8c0e8',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {parsed.body || displayOutput}
                  </div>

                  {/* Model tag */}
                  <div style={{
                    padding: '0 16px 12px',
                    display: 'flex', gap: 6,
                  }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
                      background: `${MODE_COLORS[displayMode]}12`, color: MODE_COLORS[displayMode],
                    }}>
                      Generated for {activeHistoryItem?.modelLabel || currentRealModel?.label}
                    </span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
                      background: 'var(--s3)', color: 'var(--t3)',
                    }}>
                      {MODE_LABELS[displayMode]}
                    </span>
                  </div>
                </div>

                {/* New Prompt button */}
                {!isGenerating && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                    <button
                      onClick={handleNewPrompt}
                      className="gbtn"
                      style={{
                        width: 'auto', padding: '12px 32px',
                        background: 'var(--s2)', border: '1px solid var(--b2)',
                        boxShadow: 'none',
                      }}
                    >
                      ✨ New Prompt
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── History Sidebar (inside main area, right side) ── */}
          <div
            className={`prompt-history-sidebar${mobileTab !== 'history' ? ' prompt-mob-hidden-hist' : ''}`}
            style={{
              width: 240, minWidth: 240, maxHeight: 'calc(100vh - 140px)',
              display: 'flex', flexDirection: 'column', gap: 6,
              borderLeft: '1px solid var(--b1)', paddingLeft: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t2)' }}>
                History
              </div>
              {history.length > 0 && (
                <button 
                  onClick={() => setShowClearConfirm(true)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--rd)', fontSize: 11, cursor: 'pointer', padding: 0 }}
                >
                  Clear all
                </button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', paddingRight: 4, flex: 1 }}>
            {historyLoading ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--t3)', fontSize: 12 }}>
                Loading history…
              </div>
            ) : history.length === 0 ? (
              <div style={{
                padding: 32, textAlign: 'center', color: 'var(--t3)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
              }}>
                <div style={{ fontSize: 28, opacity: 0.3 }}>📝</div>
                <div style={{ fontSize: 12, lineHeight: 1.6 }}>Your generated prompts will appear here</div>
              </div>
            ) : (
              history.map(item => {
                const isActive = activeHistoryItem && (activeHistoryItem.$id === item.$id || activeHistoryItem.id === item.id);
                return (
                  <div
                    key={item.$id || item.id}
                    onClick={() => handleHistoryClick(item)}
                    style={{
                      padding: '10px 12px', borderRadius: 'var(--r)',
                      border: `1px solid ${isActive ? 'var(--ac)' : 'var(--b2)'}`,
                      background: isActive ? 'rgba(109,40,217,.08)' : 'var(--s2)',
                      cursor: 'pointer', transition: 'var(--transition)',
                      position: 'relative',
                    }}
                  >
                    <div style={{
                      fontSize: 12, fontWeight: 700, color: 'var(--t1)',
                      marginBottom: 4, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                    }}>
                      {item.name || 'Untitled'}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                      <span style={{
                        fontSize: 8, fontWeight: 800, padding: '1px 6px', borderRadius: 99,
                        background: `${MODE_COLORS[item.mode] || '#666'}18`,
                        color: MODE_COLORS[item.mode] || 'var(--t3)',
                      }}>
                        {MODE_LABELS[item.mode] || item.mode}
                      </span>
                      <span style={{
                        fontSize: 8, fontWeight: 600, padding: '1px 6px', borderRadius: 99,
                        background: 'var(--s3)', color: 'var(--t3)',
                      }}>
                        {item.modelLabel || item.modelId}
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--t3)' }}>
                      {relativeTime(item.timestamp || new Date(item.createdAt || item.$createdAt).getTime())}
                    </div>
                    {/* Delete button */}
                    <button
                      onClick={(e) => handleDeleteHistory(item.$id || item.id, e)}
                      style={{
                        position: 'absolute', top: 6, right: 6,
                        width: 20, height: 20, borderRadius: 4,
                        border: 'none', background: 'transparent',
                        color: 'var(--t3)', cursor: 'pointer', fontSize: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: 0.4, transition: 'var(--transition)',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#f87171'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; e.currentTarget.style.color = 'var(--t3)'; }}
                    >
                      ✕
                    </button>
                  </div>
                );
              })
            )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile Bottom Nav Bar ── */}
      <div className="prompt-mob-nav">
        <button
          className={`prompt-mob-btn${mobileTab === 'writer' ? ' on' : ''}`}
          onClick={() => setMobileTab('writer')}
          title="Writer"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9"/>
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
          <span>Writer</span>
        </button>
        <button
          className={`prompt-mob-btn${mobileTab === 'result' ? ' on' : ''}`}
          onClick={() => setMobileTab('result')}
          title="Result"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M3 9h18M9 21V9"/>
          </svg>
          <span>Result</span>
        </button>
        <button
          className={`prompt-mob-btn${mobileTab === 'history' ? ' on' : ''}`}
          onClick={() => setMobileTab('history')}
          title="History"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
            <path d="M12 7v5l4 2"/>
          </svg>
          <span>History</span>
        </button>
      </div>

      {showClearConfirm && (
        <div className="mp-overlay" onClick={() => setShowClearConfirm(false)} style={{ zIndex: 80 }}>
          <div className="logout-confirm" onClick={e => e.stopPropagation()}>
            <div className="logout-icon" style={{ fontSize: 32, marginBottom: 8, textAlign: 'center' }}>🗑️</div>
            <div className="logout-title">Clear History?</div>
            <div className="logout-desc">Are you sure you want to delete your entire prompt history? This action cannot be undone.</div>
            <div className="logout-btns">
              <button className="logout-btn cancel" onClick={() => setShowClearConfirm(false)}>
                Cancel
              </button>
              <button className="logout-btn confirm" onClick={async () => {
                setShowClearConfirm(false);
                await handleClearAll();
              }}>
                <Icons.Trash sz={13} /> Clear all
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
